<template>
  <div class="purchase-online-oripa-modal">
    <p class="purchase-online-oripa-modal__title">
      {{ title }}
    </p>
    <div class="purchase-online-oripa-modal__total-price-area">
      <p class="purchase-online-oripa-modal__amount">{{ oripaUnit }}口購入</p>
      <p class="purchase-online-oripa-modal__total-price">
        ¥{{ totalPrice.toLocaleString() }}
      </p>
    </div>
    <div class="credit-card-form">
      <div class="credit-card-form__top">
        <div class="credit-card-form__center-box">
          <div class="credit-card-form__title">クレジットカード</div>
          <div
            v-if="credit_card_registered"
            class="credit-card-form__active-credit-card"
          >
            <img src="/images/icon_checked-circle-filled-green.svg" />
            <img
              class="credit-card-form__active-credit-card-image"
              :src="activeCreditCardUrl"
            />
            <p class="credit-card-form__active-credit-card-no">
              {{ `************${credit_card_no}` }}
            </p>
          </div>
          <div v-else class="credit-card-form__credit-card-logos">
            <img src="/images/icon_checked-circle-filled.svg" />
            <div>
              <img
                class="credit-card-form__credit-card-logo"
                :src="visaImageUrl"
              />
              <img
                class="credit-card-form__credit-card-logo"
                :src="masterImageUrl"
              />
              <img
                class="credit-card-form__credit-card-logo"
                :src="jcbImageUrl"
              />
              <img
                class="credit-card-form__credit-card-logo"
                :src="amexImageUrl"
              />
              <img
                class="credit-card-form__credit-card-logo"
                :src="dinersImageUrl"
              />
              未登録
              <p v-if="credit_card_registered" class="credit-card-form__expire">
                {{ credit_card_expire }}
              </p>
              <p v-else class="credit-card-form__attention">
                カードを登録してください。
              </p>
            </div>
          </div>
        </div>
        <a
          class="credit-card-form__arrow-right-icon-wrapper"
          :href="`/mypage/credit_card/edit?lottery_id=${lottery_id}`"
        >
          <i class="jirafficon jirafficon-arrow-right inline-icon" />
        </a>
      </div>
    </div>
    <div class="form" :class="$style.payPoints">
      <div class="form__group form__group--column">
        <div class="form__label">利用ポイント</div>
        <div class="form__control">
          <input
            v-model="payPoints"
            class="form__input"
            type="number"
            placeholder="入力してください"
            name="lottery_result[pay_points]"
            :disabled="allPoints"
            @change="updateBillingAmountHandler"
          />
        </div>
      </div>
      <div class="form__group">
        <div class="form__control">
          <div class="form__checkbox-label">
            利用可能ポイント: {{ points.toLocaleString() }}ポイント
          </div>
          <label class="form__checkbox-label">
            <input
              v-model="allPoints"
              type="checkbox"
              class="form__checkbox"
              @change="changeAllCheckHandler"
            />
            利用可能ポイントをすべて使う
          </label>
        </div>
      </div>
    </div>
    <div :class="$style.formappamount">
      <div :class="$style.list">
        <b :class="$style.b">{{ estimates.subject.label }}</b>
        <b :class="$style.b">{{ estimates.subject.value }}</b>
      </div>
      <div :class="$style.list1">
        <div
          v-for="detail in estimates.details"
          :key="detail.label"
          :class="$style.parent"
        >
          <div :class="$style.div">
            {{ detail.label }}
          </div>
          <div :class="$style.div">
            {{ detail.value }}
          </div>
        </div>
      </div>
    </div>
    <div class="purchase-online-oripa-modal__purchase-button-wrapper">
      <button
        :class="[
          'purchase-online-oripa-modal__purchase-button btn',
          canPurchase ? 'btn--primary' : 'btn--disabled',
        ]"
        :disabled="!canPurchase"
        @click="purchaseOnlineOripa()"
      >
        購入する
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import { AxiosResponse } from 'axios'
import {
  postOripaLotteryResult,
  getOripaLotteriesBillingAmount,
} from '~/api/oripa/lottery_results/new'

interface Data {
  totalPrice: number
  isPurchased: boolean
  payPoints: number
  estimates: object
  allPoints: boolean
  requestKey: string
}

interface Methods {
  purchaseOnlineOripa(): void
  updateBillingAmountHandler(): void
  changeAllCheckHandler(): void
}

interface Computed {
  oripaUnit: number
  visaImageUrl: string
  masterImageUrl: string
  jcbImageUrl: string
  amexImageUrl: string
  dinersImageUrl: string
  activeCreditCardUrl: string
  canPurchase: boolean
  billingAmount: number
}

interface Props {
  title: string
  lottery_id: number
  amount: number
  points: number
  price: number
  remaining: number
  credit_card_brand: string
  credit_card_no: string
  credit_card_expire: string
  credit_card_registered: boolean
}

export default defineComponent<Data, Methods, Computed, Props>({
  props: {
    title: {
      type: String,
      required: true,
    },
    lottery_id: {
      type: Number,
      required: true,
    },
    amount: {
      type: Number,
      required: true,
    },
    points: {
      type: Number,
      required: true,
    },
    price: {
      type: Number,
      required: true,
    },
    remaining: {
      type: Number,
      required: true,
    },
    credit_card_brand: {
      type: String,
    },
    credit_card_no: {
      type: String,
    },
    credit_card_expire: {
      type: String,
    },
    credit_card_registered: {
      type: Boolean,
    },
  },
  data() {
    return {
      totalPrice: 0,
      isPurchased: false,
      payPoints: 0,
      estimates: {
        subject: { label: '', value: '' },
        details: [{ label: '', value: '' }],
      },
      allPoints: false,
      requestKey: '',
    }
  },
  computed: {
    oripaUnit(): number {
      const ONE_TICKET = 1
      const TEN_TICKET = 10
      if (this.amount === ONE_TICKET) {
        return this.amount
      }

      if (this.amount === TEN_TICKET && this.amount > this.remaining) {
        return this.remaining
      }

      return this.amount
    },
    visaImageUrl(): string {
      return '/images/icon_visa.png'
    },
    masterImageUrl(): string {
      return '/images/icon_mastercard.png'
    },
    jcbImageUrl(): string {
      return '/images/icon_jcb.gif'
    },
    amexImageUrl(): string {
      return '/images/icon_amex.png'
    },
    dinersImageUrl(): string {
      return '/images/icon_diners.png'
    },
    activeCreditCardUrl(): string {
      switch (this.credit_card_brand) {
        case 'VISA':
          return this.visaImageUrl
        case 'JCB':
          return this.jcbImageUrl
        case 'MASTERCARD':
          return this.masterImageUrl
        case 'AMEX':
          return this.amexImageUrl
        case 'DINERS':
          return this.dinersImageUrl
        default:
          return ''
      }
    },
    canPurchase(): boolean {
      return (
        (this.credit_card_registered || this.payPoints === this.totalPrice) &&
        !this.isPurchased
      )
    },
    billingAmount(): number {
      return 100
    },
  },
  watch: {
    payPoints(value: number | null) {
      if (value === null) {
        return
      }
      if (value > this.points) {
        this.payPoints = this.points
      }
      if (value < 0) {
        this.payPoints = 0
      }
      if (value > this.totalPrice) {
        this.payPoints = this.totalPrice
      }
    },
  },
  created() {
    this.totalPrice = this.price * this.oripaUnit
    this.updateBillingAmountHandler()
  },
  methods: {
    purchaseOnlineOripa(): void {
      this.isPurchased = true
      postOripaLotteryResult(
        this.lottery_id,
        this.oripaUnit.toString(),
        this.totalPrice.toString(),
        this.payPoints.toString()
      )
        .then(
          (response: AxiosResponse<Api.Oripa.LotteryResults.Post.Response>) => {
            window.location.href = response.data.url
          }
        )
        .catch(() => {
          this.isPurchased = false
        })
    },
    async updateBillingAmountHandler(): Promise<void> {
      this.requestKey = uuidv4()
      const result = await getOripaLotteriesBillingAmount(
        this.lottery_id,
        this.oripaUnit,
        this.payPoints,
        this.requestKey
      )
      if (result.data.request_key === this.requestKey) {
        const [estimates] = result.data.estimates
        this.estimates = estimates
      }
    },
    changeAllCheckHandler(): void {
      if (this.allPoints) {
        if (this.points >= this.totalPrice) {
          this.payPoints = this.totalPrice
        } else {
          this.payPoints = this.points
        }
        this.updateBillingAmountHandler()
      }
    },
  },
})
</script>

<style scoped lang="scss">
.purchase-online-oripa-modal {
  padding: $space-XL;

  &__title {
    margin-bottom: $space-LL;
    font-size: $font-size-L;
    font-weight: bold;
  }

  &__total-price-area {
    margin-bottom: $space-LL;
    display: flex;
    justify-content: center;
  }

  &__amount {
    margin-right: $space-M;
    font-size: $font-size-M;
    font-weight: bold;
  }

  &__total-price {
    font-size: $font-size-3XL;
    font-weight: bold;
  }

  &__purchase-button-wrapper {
    text-align: center;
  }

  &__purchase-button {
    min-width: 280px;
  }
}

.credit-card-form {
  margin-bottom: $space-LL;
  &__title {
    align-self: stretch;
    position: relative;
    font-weight: bold;
  }
  &__top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: $space-S;
    padding: $space-L;
    background-color: $co-bg-gray;
  }

  &__active-credit-card {
    display: flex;
  }

  &__active-credit-card-image {
    margin-right: $space-XS;
    width: auto;
    height: 18px;
  }

  &__active-credit-card-no {
    font-size: $font-size-XXS;
  }

  &__credit-card-logos {
    display: flex;
    margin-bottom: $space-S;
  }

  &__credit-card-logo {
    margin-right: $space-XS;
    width: auto;
    height: 18px;
  }

  &__credit-card-status {
    font-size: $font-size-XS;
  }

  &__expire {
    font-size: $font-size-S;
  }

  &__attention {
    font-size: $font-size-S;
    color: $co-red;
  }

  &__arrow-right-icon-wrapper {
    display: block;
    color: $co-dark-gray;
  }

  &__description {
    font-size: $font-size-S;
    color: $co-dark-gray;
    text-align: center;
  }
}
</style>
<style module lang="scss">
.b {
  position: relative;
  line-height: 150%;
}
.list {
  align-self: stretch;
  border-bottom: 1px solid $co-border;
  overflow: hidden;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: $space-M 0px;
}
.div {
  position: relative;
  line-height: 150%;
  font-weight: 500;
}
.parent {
  align-self: stretch;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}
.list1 {
  align-self: stretch;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  padding: $space-M 0px;
  font-size: $font-size-S;
}
.formappamount {
  width: 100%;
  position: relative;
  border-radius: $round;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 0px 16px;
  box-sizing: border-box;
  text-align: left;
  font-size: $font-size-M;
  color: $co-dark-gray;
  font-family: 'Yu Gothic';
}
.payPoints {
  background-color: $co-bg;
  padding: 0 $space-M;
  input[type='number']:disabled {
    background: $co-light-gray;
  }
}
</style>
